import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { BarChart3, ListChecks, PlayCircle, Plus, Settings, Upload } from 'lucide-react';

const DOMAIN_SCOPES = ['sources', 'search_results', 'any'];
const KEYWORD_SCOPES = ['answer', 'question', 'brand_entities', 'fan_out_queries', 'any'];
const RESPONSE_PLATFORM_OPTIONS = [
  { id: 'chat_gpt', label: 'ChatGPT' },
  { id: 'claude', label: 'Claude' },
  { id: 'gemini', label: 'Gemini' },
  { id: 'perplexity', label: 'Perplexity' }
];
const MAX_PROMPT_CHARS = 500;
const DEFAULT_SCHEDULE_OPTIONS = [
  { id: 'weekly_mon_06', label: 'Weekly — Mondays at 06:00 UTC' },
  { id: 'weekly_mon_09', label: 'Weekly — Mondays at 09:00 UTC' },
  { id: 'weekly_fri_06', label: 'Weekly — Fridays at 06:00 UTC' },
  { id: 'daily_06', label: 'Daily at 06:00 UTC' }
];

const formatNextRun = (value) => {
  if (!value) return 'Not scheduled';
  return String(value).replace('T', ' ').slice(0, 19) + ' UTC';
};

const emptyTarget = {
  entity_type: 'brand',
  label: '',
  domain: '',
  aliases: '',
  search_scope: 'answer',
  search_filter: 'include',
  is_active: true
};

const emptyPrompt = {
  prompt_text: '',
  tags: '',
  market: '',
  locale: '',
  priority: 0,
  is_active: true
};

const parseCsvImportResponse = (data) => {
  const inserted = Number(data?.inserted || 0);
  const skipped = Number(data?.skipped || 0);
  return { inserted, skipped };
};

const AILlmMentionsDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [config, setConfig] = useState(null);
  const [targets, setTargets] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [runs, setRuns] = useState([]);
  const [dashboard, setDashboard] = useState({ daily: [], top_domains: [], recent_results: [] });
  const [platformDraft, setPlatformDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [runningMentions, setRunningMentions] = useState(false);
  const [runningPrompts, setRunningPrompts] = useState(false);
  const [targetForm, setTargetForm] = useState(emptyTarget);
  const [promptForm, setPromptForm] = useState(emptyPrompt);
  const [importFile, setImportFile] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [readiness, setReadiness] = useState({ ok: false, targetCount: 0, promptCount: 0, error: null });

  const loadClients = useCallback(async () => {
    const res = await axios.get('/api/clients?include_inactive=true');
    const rows = (res.data || []).map((client) => ({
      id: String(client.id),
      name: client.name,
      status: client.status
    }));
    rows.sort((a, b) => a.name.localeCompare(b.name));
    setClients(rows);
    if (!selectedClientId && rows.length > 0) {
      setSelectedClientId(rows[0].id);
    }
  }, [selectedClientId]);

  const loadAiData = useCallback(async (clientId) => {
    if (!clientId) return;
    setLoading(true);
    try {
      const [configRes, targetsRes, promptsRes, runsRes, dashboardRes, readinessRes] = await Promise.all([
        axios.get(`/api/ai-llm-mentions/config/${clientId}`),
        axios.get(`/api/ai-llm-mentions/targets/${clientId}`),
        axios.get(`/api/ai-llm-mentions/prompts/${clientId}`),
        axios.get(`/api/ai-llm-mentions/runs/${clientId}`),
        axios.get(`/api/ai-llm-mentions/dashboard/${clientId}`),
        axios.get(`/api/ai-llm-mentions/readiness/${clientId}`)
      ]);
      setConfig(configRes.data || null);
      setPlatformDraft(Array.isArray(configRes.data?.default_platforms) ? configRes.data.default_platforms.join(', ') : '');
      setTargets(targetsRes.data || []);
      setPrompts(promptsRes.data || []);
      setRuns(runsRes.data || []);
      setDashboard(dashboardRes.data || { daily: [], top_domains: [], recent_results: [] });
      setReadiness(readinessRes.data || { ok: false, targetCount: 0, promptCount: 0, error: null });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load AI LLM Mentions data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClients().catch(() => {
      toast.error('Failed to load clients');
      setLoading(false);
    });
  }, [loadClients]);

  useEffect(() => {
    if (!selectedClientId) return;
    loadAiData(selectedClientId);
  }, [selectedClientId, loadAiData]);

  const availableScopes = useMemo(() => {
    const hasDomain = Boolean(String(targetForm.domain || '').trim());
    return hasDomain ? DOMAIN_SCOPES : KEYWORD_SCOPES;
  }, [targetForm.domain]);

  useEffect(() => {
    if (!availableScopes.includes(targetForm.search_scope)) {
      setTargetForm((prev) => ({
        ...prev,
        search_scope: availableScopes[0]
      }));
    }
  }, [availableScopes, targetForm.search_scope]);

  const mentionRateByPlatform = useMemo(() => {
    const grouped = new Map();
    (dashboard.daily || []).forEach((row) => {
      const platform = row.platform || 'unknown';
      if (!grouped.has(platform)) grouped.set(platform, []);
      grouped.get(platform).push(row);
    });
    return Array.from(grouped.entries()).map(([platform, rows]) => ({
      platform,
      latestRate: Number(rows[0]?.mention_rate || 0),
      latestTotal: Number(rows[0]?.total_answers || 0)
    }));
  }, [dashboard.daily]);

  const saveConfig = async (nextPatch) => {
    if (!selectedClientId || !config) return;
    setSavingConfig(true);
    try {
      const payload = {
        is_enabled: config.is_enabled,
        default_platforms: Array.isArray(config.default_platforms)
          ? config.default_platforms
          : String(platformDraft || '')
            .split(',')
            .map((entry) => entry.trim())
            .filter(Boolean),
        repeat_count: config.repeat_count,
        schedule_cron: config.schedule_cron,
        market: config.market,
        response_platforms: Array.isArray(config.response_platforms)
          ? config.response_platforms
          : ['chat_gpt', 'claude', 'gemini', 'perplexity'],
        web_search_enabled: config.web_search_enabled !== false,
        schedule_enabled: Boolean(config.schedule_enabled),
        schedule_key: config.schedule_key || 'weekly_mon_06',
        ...nextPatch
      };
      const response = await axios.put(`/api/ai-llm-mentions/config/${selectedClientId}`, payload);
      setConfig(response.data);
      toast.success('AI mentions configuration saved');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const addTarget = async (e) => {
    e.preventDefault();
    if (!selectedClientId) return;
    try {
      await axios.post(`/api/ai-llm-mentions/targets/${selectedClientId}`, {
        ...targetForm,
        aliases: String(targetForm.aliases || '')
          .split(',')
          .map((entry) => entry.trim())
          .filter(Boolean),
        search_scope: [targetForm.search_scope]
      });
      setTargetForm(emptyTarget);
      toast.success('Target added');
      loadAiData(selectedClientId);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add target');
    }
  };

  const deleteTarget = async (targetId) => {
    if (!selectedClientId) return;
    try {
      await axios.delete(`/api/ai-llm-mentions/targets/${selectedClientId}/${targetId}`);
      toast.success('Target deleted');
      loadAiData(selectedClientId);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete target');
    }
  };

  const addPrompt = async (e) => {
    e.preventDefault();
    if (!selectedClientId) return;
    try {
      await axios.post(`/api/ai-llm-mentions/prompts/${selectedClientId}`, {
        ...promptForm,
        tags: String(promptForm.tags || '')
          .split(',')
          .map((entry) => entry.trim())
          .filter(Boolean)
      });
      setPromptForm(emptyPrompt);
      toast.success('Prompt added');
      loadAiData(selectedClientId);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add prompt');
    }
  };

  const deletePrompt = async (promptId) => {
    if (!selectedClientId) return;
    try {
      await axios.delete(`/api/ai-llm-mentions/prompts/${selectedClientId}/${promptId}`);
      toast.success('Prompt deleted');
      loadAiData(selectedClientId);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete prompt');
    }
  };

  const importPrompts = async (e) => {
    e.preventDefault();
    if (!selectedClientId || !importFile) {
      toast.error('Choose a CSV file first');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const response = await axios.post(`/api/ai-llm-mentions/prompts/${selectedClientId}/import`, formData);
      const stats = parseCsvImportResponse(response.data);
      toast.success(`Import complete. Inserted: ${stats.inserted}, Skipped: ${stats.skipped}`);
      setImportFile(null);
      loadAiData(selectedClientId);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to import prompts');
    }
  };

  const queueRun = async (mode) => {
    if (!selectedClientId) return;
    if (mode === 'mentions' && !readiness.mentionsReady) {
      toast.error(readiness.error || 'Add at least one target under the Targets tab before running mentions search.');
      setActiveTab('targets');
      return;
    }
    if (mode === 'prompts' && !readiness.promptsReady) {
      toast.error(readiness.error || 'Add at least one prompt under the Prompts tab before running LLM responses.');
      setActiveTab('prompts');
      return;
    }
    const setBusy = mode === 'prompts' ? setRunningPrompts : setRunningMentions;
    setBusy(true);
    try {
      await axios.post(`/api/ai-llm-mentions/runs/${selectedClientId}`, { mode });
      toast.success(mode === 'prompts'
        ? 'Prompt run queued — check the Runs tab. Live LLM calls can take a few minutes.'
        : 'Mentions run queued — check the Runs tab in a few seconds.');
      setActiveTab('runs');
      setTimeout(() => {
        loadAiData(selectedClientId);
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to queue run');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI LLM Mentions</h1>
          <p className="text-sm text-gray-600">Track brand visibility in AI answers — mentions search plus live prompt runs across ChatGPT, Claude, Gemini and Perplexity.</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="input min-w-[240px]"
            value={selectedClientId}
            onChange={(e) => setSelectedClientId(e.target.value)}
          >
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name} ({client.status})
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-secondary inline-flex flex-row items-center justify-center gap-2 h-10 px-4 whitespace-nowrap shrink-0"
            onClick={() => queueRun('mentions')}
            disabled={runningMentions || !selectedClientId}
          >
            <PlayCircle className="h-4 w-4" />
            {runningMentions ? 'Queueing...' : 'Run Mentions'}
          </button>
          <button
            type="button"
            className="btn btn-primary inline-flex flex-row items-center justify-center gap-2 h-10 px-4 whitespace-nowrap shrink-0"
            onClick={() => queueRun('prompts')}
            disabled={runningPrompts || !selectedClientId}
          >
            <PlayCircle className="h-4 w-4" />
            {runningPrompts ? 'Queueing...' : 'Run Prompts'}
          </button>
        </div>
      </div>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', name: 'Overview', icon: BarChart3 },
            { id: 'targets', name: 'Targets', icon: ListChecks },
            { id: 'prompts', name: 'Prompts', icon: Upload },
            { id: 'configuration', name: 'Configuration', icon: Settings },
            { id: 'runs', name: 'Runs', icon: PlayCircle }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.name}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {loading ? (
        <div className="card"><p className="text-gray-600">Loading AI mentions workspace...</p></div>
      ) : (
        <>
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="card bg-blue-50 border border-blue-100">
                <h3 className="text-lg font-medium text-gray-900 mb-2">How to use this tool</h3>
                <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1">
                  <li><strong>Targets</strong> — add the client brand and competitors. These drive mentions search and tell prompt runs which names to look for in answers.</li>
                  <li><strong>Prompts</strong> — add the questions customers would ask an LLM (max 500 characters). <strong>Run Prompts</strong> sends each one live to ChatGPT, Claude, Gemini and Perplexity.</li>
                  <li><strong>Run Mentions</strong> — searches DataForSEO’s existing AI mention index for Google AI Overview and ChatGPT (v1).</li>
                </ol>
                {!readiness.ok && (
                  <p className="mt-3 text-sm text-amber-800">
                    Setup needed: {readiness.error || 'Add at least one target or prompt before running.'}
                  </p>
                )}
                {readiness.ok && (
                  <p className="mt-3 text-sm text-green-800">
                    Ready: {readiness.targetCount} target(s){readiness.mentionsReady ? ' (mentions search)' : ''}, {readiness.promptCount} prompt(s){readiness.promptsReady ? ' (live LLM runs)' : ''}.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="card">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Latest Mention Rate by Platform</h3>
                  {mentionRateByPlatform.length === 0 ? (
                    <p className="text-sm text-gray-500">No run data yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {mentionRateByPlatform.map((row) => (
                        <div key={row.platform} className="flex items-center justify-between border border-gray-200 rounded px-3 py-2">
                          <span className="text-sm text-gray-700">{row.platform}</span>
                          <span className="text-sm font-medium text-gray-900">{row.latestRate.toFixed(2)}% ({row.latestTotal} answers)</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="card">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Top Cited Domains</h3>
                  {Array.isArray(dashboard.top_domains) && dashboard.top_domains.length > 0 ? (
                    <div className="max-h-72 overflow-auto space-y-2">
                      {dashboard.top_domains.map((row) => (
                        <div key={row.domain} className="flex items-center justify-between border border-gray-200 rounded px-3 py-2">
                          <span className="text-sm text-gray-700">{row.domain}</span>
                          <span className="text-sm font-medium text-gray-900">{Number(row.mentions || 0)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No citation data yet.</p>
                  )}
                </div>
              </div>

              <div className="card">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Recent Questions &amp; Answers</h3>
                {Array.isArray(dashboard.recent_results) && dashboard.recent_results.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Platform</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Question</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Answer</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mentioned</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" title="Mentions: DataForSEO source rank. Prompt runs: order of the first citation of the client domain.">Client Pos</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Run</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {dashboard.recent_results.map((row) => (
                          <tr key={row.id}>
                            <td className="px-4 py-3 text-sm text-gray-700 align-top">{row.platform || '—'}</td>
                            <td className="px-4 py-3 text-sm text-gray-500 align-top">{row.model_name || '—'}</td>
                            <td className="px-4 py-3 text-sm text-gray-900 align-top max-w-xs">{row.question || '—'}</td>
                            <td className="px-4 py-3 text-sm text-gray-700 align-top max-w-md whitespace-pre-wrap">
                              {(row.answer_markdown || '—').slice(0, 280)}{(row.answer_markdown || '').length > 280 ? '…' : ''}
                            </td>
                            <td className="px-4 py-3 text-sm align-top">
                              {row.mentioned_brand ? (
                                <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Yes</span>
                              ) : (
                                <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">No</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 align-top whitespace-nowrap">
                              {row.client_source_position == null ? '—' : `#${row.client_source_position}`}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 align-top whitespace-nowrap">
                              {row.run_at ? String(row.run_at).replace('T', ' ').slice(0, 19) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No Q&amp;A results yet. Run a scan after adding targets.</p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'targets' && (
            <div className="space-y-6">
              <div className="card bg-gray-50 border border-gray-200">
                <p className="text-sm text-gray-700">
                  <strong>Required for Run Mentions:</strong> add a <em>keyword</em> target (label only, e.g. <em>RacingTV</em>) with scope <em>answer</em> or <em>question</em> to search AI-generated text.
                  Add a separate <em>domain</em> target (e.g. <em>racingtv.com</em>) with scope <em>sources</em> to track citations. Domain targets cannot use answer/question scopes — DataForSEO treats those as keyword-only.
                  Brand targets are also used to detect mentions in live prompt answers.
                </p>
              </div>
              <div className="card">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Add Target</h3>
                <form onSubmit={addTarget} className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
                  <select className="input" value={targetForm.entity_type} onChange={(e) => setTargetForm((prev) => ({ ...prev, entity_type: e.target.value }))}>
                    <option value="brand">Brand</option>
                    <option value="competitor">Competitor</option>
                  </select>
                  <input className="input" placeholder="Label" value={targetForm.label} onChange={(e) => setTargetForm((prev) => ({ ...prev, label: e.target.value }))} />
                  <input className="input" placeholder="Domain (optional)" value={targetForm.domain} onChange={(e) => setTargetForm((prev) => ({ ...prev, domain: e.target.value }))} />
                  <input className="input" placeholder="Aliases (comma separated)" value={targetForm.aliases} onChange={(e) => setTargetForm((prev) => ({ ...prev, aliases: e.target.value }))} />
                  <select className="input" value={targetForm.search_scope} onChange={(e) => setTargetForm((prev) => ({ ...prev, search_scope: e.target.value }))}>
                    {availableScopes.map((scope) => (
                      <option key={scope} value={scope}>{scope}</option>
                    ))}
                  </select>
                  <button type="submit" className="btn btn-primary inline-flex items-center justify-center">
                    <Plus className="h-4 w-4 mr-2" />
                    Add
                  </button>
                </form>
              </div>

              <div className="card">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Configured Targets</h3>
                {targets.length === 0 ? (
                  <p className="text-sm text-gray-500">No targets configured yet.</p>
                ) : (
                  <div className="space-y-2">
                    {targets.map((target) => (
                      <div key={target.id} className="border border-gray-200 rounded p-3 flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{target.label} <span className="text-gray-500">({target.entity_type})</span></p>
                          <p className="text-xs text-gray-500">
                            Domain: {target.domain || '—'} | Scope: {Array.isArray(target.search_scope) ? target.search_scope.join(', ') : '—'}
                          </p>
                        </div>
                        <button type="button" className="btn btn-secondary" onClick={() => deleteTarget(target.id)}>Delete</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'prompts' && (
            <div className="space-y-6">
              <div className="card bg-gray-50 border border-gray-200">
                <p className="text-sm text-gray-700">
                  <strong>Required for Run Prompts:</strong> add customer-style questions (max {MAX_PROMPT_CHARS} characters — a DataForSEO limit).
                  Each active prompt is sent live to the selected prompt platforms. Keep Repeat Count at 1 until you have reviewed cost.
                </p>
              </div>
              <div className="card">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Add Prompt</h3>
                <form onSubmit={addPrompt} className="space-y-3">
                  <textarea className="input min-h-[100px]" placeholder="Prompt text..." value={promptForm.prompt_text} onChange={(e) => setPromptForm((prev) => ({ ...prev, prompt_text: e.target.value }))} maxLength={MAX_PROMPT_CHARS} />
                  <p className="text-xs text-gray-500">{String(promptForm.prompt_text || '').length} / {MAX_PROMPT_CHARS} characters</p>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <input className="input" placeholder="Tags (comma separated)" value={promptForm.tags} onChange={(e) => setPromptForm((prev) => ({ ...prev, tags: e.target.value }))} />
                    <input className="input" placeholder="Market (e.g. UK)" value={promptForm.market} onChange={(e) => setPromptForm((prev) => ({ ...prev, market: e.target.value }))} />
                    <input className="input" placeholder="Locale (e.g. en-GB)" value={promptForm.locale} onChange={(e) => setPromptForm((prev) => ({ ...prev, locale: e.target.value }))} />
                    <input type="number" className="input" placeholder="Priority" value={promptForm.priority} onChange={(e) => setPromptForm((prev) => ({ ...prev, priority: Number(e.target.value) }))} />
                  </div>
                  <button type="submit" className="btn btn-primary">Add Prompt</button>
                </form>
              </div>

              <div className="card">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Import Prompts (CSV)</h3>
                <p className="text-sm text-gray-600 mb-1">Expected columns: `prompt` or `prompt_text` (optional: tags, market, locale, priority).</p>
                <p className="text-sm text-gray-500 mb-3">Prompts over {MAX_PROMPT_CHARS} characters are skipped. Run Prompts sends the library to DataForSEO LLM Responses (ChatGPT, Claude, Gemini, Perplexity).</p>
                <form onSubmit={importPrompts} className="flex flex-wrap items-center gap-3">
                  <input type="file" accept=".csv,text/csv" onChange={(e) => setImportFile(e.target.files?.[0] || null)} className="input" />
                  <button type="submit" className="btn btn-secondary inline-flex items-center">
                    <Upload className="h-4 w-4 mr-2" />
                    Import CSV
                  </button>
                </form>
              </div>

              <div className="card">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Prompt Library</h3>
                {prompts.length === 0 ? (
                  <p className="text-sm text-gray-500">No prompts added yet.</p>
                ) : (
                  <div className="space-y-2">
                    {prompts.map((prompt) => (
                      <div key={prompt.id} className="border border-gray-200 rounded p-3 flex items-start justify-between gap-4">
                        <div>
                          <p className="text-sm text-gray-900">{prompt.prompt_text}</p>
                          <p className="text-xs text-gray-500">
                            Tags: {Array.isArray(prompt.tags) && prompt.tags.length ? prompt.tags.join(', ') : '—'} | Market: {prompt.market || '—'} | Locale: {prompt.locale || '—'}
                          </p>
                        </div>
                        <button type="button" className="btn btn-secondary" onClick={() => deletePrompt(prompt.id)}>Delete</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'configuration' && (
            <div className="space-y-6">
              <div className="card">
                <h3 className="text-lg font-medium text-gray-900 mb-4">General</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <label className="text-sm">
                    <span className="label">Client enabled</span>
                    <input
                      type="checkbox"
                      className="mt-2"
                      checked={Boolean(config?.is_enabled)}
                      onChange={(e) => saveConfig({ is_enabled: e.target.checked })}
                      disabled={savingConfig}
                    />
                    <span className="text-xs text-gray-500 mt-1 block">Master switch. When off, scheduled runs are skipped for this client.</span>
                  </label>
                  <label className="text-sm">
                    <span className="label">Market</span>
                    <select
                      className="input"
                      value={config?.market || 'us'}
                      onChange={(e) => saveConfig({ market: e.target.value })}
                      disabled={savingConfig}
                    >
                      <option value="us">United States</option>
                      <option value="uk">United Kingdom</option>
                    </select>
                    <span className="text-xs text-gray-500 mt-1 block">Used by mentions search and prompt web-search localisation. ChatGPT mention data is US/English only.</span>
                  </label>
                  <label className="text-sm">
                    <span className="label">Repeat count</span>
                    <input
                      type="number"
                      className="input"
                      value={Number(config?.repeat_count || 1)}
                      min={1}
                      max={10}
                      onChange={(e) => setConfig((prev) => ({ ...prev, repeat_count: Number(e.target.value) }))}
                      onBlur={(e) => saveConfig({ repeat_count: Number(e.target.value || 1) })}
                    />
                    <span className="text-xs text-gray-500 mt-1 block">How many times each prompt/platform combination is run. Keep at 1 for one-off reports.</span>
                  </label>
                </div>
              </div>

              <div className="card">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Mentions search</h3>
                <label className="text-sm block max-w-xl">
                  <span className="label">Platforms (comma separated)</span>
                  <input
                    className="input"
                    value={platformDraft}
                    onBlur={(e) => {
                      const platforms = e.target.value.split(',').map((entry) => entry.trim()).filter(Boolean);
                      saveConfig({ default_platforms: platforms.length ? platforms : ['google', 'chat_gpt'] });
                    }}
                    onChange={(e) => setPlatformDraft(e.target.value)}
                  />
                  <span className="text-xs text-gray-500 mt-1 block">Supported: google, chat_gpt. Used by Run Mentions only.</span>
                </label>
              </div>

              <div className="card">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Prompt runs</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="text-sm">
                    <span className="label">Platforms</span>
                    <div className="flex flex-wrap gap-3 mt-2">
                      {RESPONSE_PLATFORM_OPTIONS.map((option) => {
                        const selected = Array.isArray(config?.response_platforms)
                          ? config.response_platforms.includes(option.id)
                          : true;
                        return (
                          <label key={option.id} className="inline-flex items-center gap-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={selected}
                              disabled={savingConfig}
                              onChange={(e) => {
                                const current = Array.isArray(config?.response_platforms)
                                  ? config.response_platforms
                                  : RESPONSE_PLATFORM_OPTIONS.map((item) => item.id);
                                const next = e.target.checked
                                  ? Array.from(new Set([...current, option.id]))
                                  : current.filter((id) => id !== option.id);
                                saveConfig({ response_platforms: next.length ? next : [option.id] });
                              }}
                            />
                            {option.label}
                          </label>
                        );
                      })}
                    </div>
                    <span className="text-xs text-gray-500 mt-1 block">Used by Run Prompts.</span>
                  </div>
                  <label className="text-sm">
                    <span className="label">Web search on prompt runs</span>
                    <input
                      type="checkbox"
                      className="mt-2"
                      checked={config?.web_search_enabled !== false}
                      onChange={(e) => saveConfig({ web_search_enabled: e.target.checked })}
                      disabled={savingConfig}
                    />
                    <span className="text-xs text-gray-500 mt-1 block">Enable so answers include citations where the model supports it.</span>
                  </label>
                </div>
              </div>

              <div className="card">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Scheduler</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Leave this off for one-off audits. Turn it on only for clients that should run automatically.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                  <label className="text-sm">
                    <span className="label">Scheduled runs</span>
                    <input
                      type="checkbox"
                      className="mt-2"
                      checked={Boolean(config?.schedule_enabled)}
                      onChange={(e) => saveConfig({ schedule_enabled: e.target.checked })}
                      disabled={savingConfig || !config?.is_enabled}
                    />
                    <span className="text-xs text-gray-500 mt-1 block">
                      {config?.is_enabled
                        ? 'When enabled, Mentions and Prompts run automatically on the chosen cadence.'
                        : 'Turn on Client enabled first.'}
                    </span>
                  </label>
                  <label className="text-sm">
                    <span className="label">Frequency</span>
                    <select
                      className="input"
                      value={config?.schedule_key || 'weekly_mon_06'}
                      onChange={(e) => saveConfig({ schedule_key: e.target.value })}
                      disabled={savingConfig || !config?.schedule_enabled}
                    >
                      {(Array.isArray(config?.schedule_options) && config.schedule_options.length
                        ? config.schedule_options
                        : DEFAULT_SCHEDULE_OPTIONS
                      ).map((option) => (
                        <option key={option.id} value={option.id}>{option.label}</option>
                      ))}
                    </select>
                    <span className="text-xs text-gray-500 mt-1 block">Times are UTC.</span>
                  </label>
                  <div className="text-sm">
                    <span className="label">Next scheduled run</span>
                    <p className="mt-2 text-gray-900">{formatNextRun(config?.next_run_at)}</p>
                    <span className="text-xs text-gray-500 mt-1 block">
                      {config?.schedule_enabled
                        ? 'Clears automatically if you turn scheduled runs off.'
                        : 'Not scheduled — one-off / manual only.'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'runs' && (
            <div className="card">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Run History</h3>
              {runs.length === 0 ? (
                <p className="text-sm text-gray-500">No runs yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mode</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Progress</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Started</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completed</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {runs.map((run) => (
                        <tr key={run.id}>
                          <td className="px-4 py-3 text-sm text-gray-700">#{run.id}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{run.run_type}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{run.mode || 'mentions'}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{run.status}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{Number(run.completed_items || 0)} / {Number(run.total_items || 0)}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 max-w-xs">{run.error_summary || '—'}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{run.started_at ? String(run.started_at).replace('T', ' ').slice(0, 19) : '—'}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{run.completed_at ? String(run.completed_at).replace('T', ' ').slice(0, 19) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AILlmMentionsDashboard;
